/* eslint-disable react/no-unescaped-entities */
/**
 * AdminSeoWriter.jsx — Cockpit Agent SEO Confilex (admin).
 *
 * Permet de :
 *  - Voir la queue de sujets (par cluster, priorité)
 *  - Lancer la génération en 1 clic ou batch 3-5
 *  - Voir les KPIs (articles publiés/draft, top mots-clés, clusters)
 *  - Validation manuelle des drafts (lien vers /admin/articles)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Bot, ListChecks, Play, Loader2, RefreshCw, BookOpen, KeySquare,
  CheckCircle2, FileText, Sparkles, Plus, ArrowRight,
} from "lucide-react";

export default function AdminSeoWriter() {
  const [stats, setStats] = useState(null);
  const [topics, setTopics] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [busy, setBusy] = useState(false);
  const [batchN, setBatchN] = useState(3);
  const [loading, setLoading] = useState(true);
  const [showAddTopic, setShowAddTopic] = useState(false);
  const [newTopic, setNewTopic] = useState({ title: "", cluster: "", city: "", priority: 7 });

  const load = async () => {
    try {
      const [s, t, c] = await Promise.all([
        apiClient.get("/seo-writer/stats"),
        apiClient.get("/seo-writer/topics?status=queued&limit=200"),
        apiClient.get("/seo-writer/clusters"),
      ]);
      setStats(s.data); setTopics(t.data.topics || []); setClusters(c.data.clusters || []);
    } catch (e) {
      toast.error("Chargement impossible", { description: formatApiError(e?.response?.data?.detail) });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runSingle = async () => {
    setBusy(true);
    const tid = toast.loading("Génération en cours (45-90 s)...");
    try {
      const { data } = await apiClient.post("/seo-writer/run", null, { timeout: 180000 });
      toast.dismiss(tid);
      if (data.generated > 0) {
        toast.success(`Article généré`, { description: `1 draft prêt à valider` });
      } else {
        toast.error("Aucun article généré", { description: data.errors?.[0] || "File vide" });
      }
      await load();
    } catch (e) {
      toast.dismiss(tid);
      toast.error("Génération impossible", { description: formatApiError(e?.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  const runBatch = async () => {
    setBusy(true);
    const tid = toast.loading(`Génération de ${batchN} articles (3-7 min)...`);
    try {
      const { data } = await apiClient.post(`/seo-writer/run-batch?n=${batchN}`, null, { timeout: 600000 });
      toast.dismiss(tid);
      toast.success(`${data.generated} articles générés`, {
        description: data.errors?.length ? `${data.errors.length} erreur(s)` : "Tous OK",
      });
      await load();
    } catch (e) {
      toast.dismiss(tid);
      toast.error("Batch impossible", { description: formatApiError(e?.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  const seedTopics = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/seo-writer/seed");
      toast.success(`${data.inserted} nouveaux sujets ajoutés`, {
        description: `Total queue : ${data.total_topics} sujets`,
      });
      await load();
    } catch (e) {
      toast.error("Seed impossible", { description: formatApiError(e?.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const addCustomTopic = async () => {
    if (!newTopic.title.trim() || !newTopic.cluster) {
      toast.error("Titre + cluster obligatoires");
      return;
    }
    try {
      await apiClient.post("/seo-writer/topics", {
        title: newTopic.title.trim(),
        cluster: newTopic.cluster,
        city: newTopic.city || null,
        priority: newTopic.priority,
      });
      toast.success("Sujet ajouté à la file");
      setNewTopic({ title: "", cluster: "", city: "", priority: 7 });
      setShowAddTopic(false);
      await load();
    } catch (e) {
      toast.error("Ajout impossible", { description: formatApiError(e?.response?.data?.detail) });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C9A961] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar variant="light" />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="admin-seo-writer">
        {/* HEADER */}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] font-medium mb-2">
              <Bot className="inline w-3.5 h-3.5 mr-1" /> Agent SEO Confilex
            </p>
            <h1 className="font-serif text-3xl text-[#0B3A2A]">Cockpit rédacteur autonome</h1>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Génération automatique d'articles SEO premium (1300-2200 mots) via Claude Sonnet 4.5.
              Maillage interne, FAQ structurée et CTA spécialistes insérés automatiquement.
              <b className="text-[#0B3A2A]"> Validation humaine obligatoire</b> avant publication.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="btn-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Rafraîchir
          </Button>
        </div>

        {/* KPI CARDS */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KpiCard label="Sujets en file" value={stats?.topics?.queued || 0}
                   sub={`/ ${stats?.topics?.total || 0} total`} icon={ListChecks} color="#C9A961" />
          <KpiCard label="Articles générés" value={stats?.articles?.total || 0}
                   sub={`${stats?.articles?.published || 0} publiés`} icon={FileText} color="#16a34a" />
          <KpiCard label="Drafts à valider" value={stats?.articles?.draft || 0}
                   sub="en attente de revue" icon={CheckCircle2} color="#d97706" />
          <KpiCard label="Clusters actifs" value={stats?.clusters_count || 0}
                   sub="thématiques couvertes" icon={BookOpen} color="#0B3A2A" />
        </div>

        {/* CONTRÔLES */}
        <section className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Génération */}
          <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-6">
            <h2 className="font-serif text-lg text-[#0B3A2A] mb-1">Lancer la génération</h2>
            <p className="text-xs text-slate-500 mb-4">
              Les sujets sont traités par ordre de priorité décroissante puis par date d'ajout.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={runSingle} disabled={busy} className="bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white"
                      data-testid="btn-run-single">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />}
                Générer 1 article
              </Button>
              <div className="flex items-center gap-2 ml-2">
                <Button onClick={runBatch} disabled={busy} className="bg-[#C9A961] hover:bg-[#D4B478] text-[#0B3A2A]"
                        data-testid="btn-run-batch">
                  {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                  Batch de
                </Button>
                <select value={batchN} onChange={e => setBatchN(parseInt(e.target.value))} disabled={busy}
                        data-testid="select-batch-n"
                        className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
                  {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} article{n > 1 ? "s" : ""}</option>)}
                </select>
              </div>
              <Button onClick={seedTopics} disabled={busy} variant="outline" data-testid="btn-seed">
                <Plus className="w-4 h-4 mr-1.5" /> Seed les 107 sujets de base
              </Button>
            </div>
            <p className="mt-4 text-[11px] text-slate-500 italic">
              Génération ~45-90 s/article. Batch de 5 = 4-7 min. Les drafts apparaissent dans
              <Link to="/admin/contents" className="text-[#C9A961] mx-1">/admin/contents</Link>
              pour validation manuelle.
            </p>
          </div>

          {/* Ajouter sujet */}
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-serif text-lg text-[#0B3A2A]">Sujet personnalisé</h2>
              <Button size="sm" variant="ghost" onClick={() => setShowAddTopic(s => !s)}>
                {showAddTopic ? "Annuler" : <><Plus className="w-3.5 h-3.5 mr-1" /> Ajouter</>}
              </Button>
            </div>
            {showAddTopic ? (
              <div className="space-y-2">
                <input value={newTopic.title}
                  onChange={e => setNewTopic({ ...newTopic, title: e.target.value })}
                  placeholder="Titre du sujet" maxLength={200}
                  data-testid="custom-topic-title"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
                <select value={newTopic.cluster}
                  onChange={e => setNewTopic({ ...newTopic, cluster: e.target.value })}
                  data-testid="custom-topic-cluster"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
                  <option value="">— Cluster —</option>
                  {clusters.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
                <input value={newTopic.city}
                  onChange={e => setNewTopic({ ...newTopic, city: e.target.value })}
                  placeholder="Ville (optionnel)" maxLength={50}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
                <Button onClick={addCustomTopic} className="w-full bg-[#0B3A2A] text-white">
                  Ajouter à la file
                </Button>
              </div>
            ) : (
              <p className="text-xs text-slate-500">
                Ajoutez un sujet hors-clusters standards (mot-clé saisonnier, actualité…).
              </p>
            )}
          </div>
        </section>

        {/* BY CLUSTER + KEYWORDS */}
        <section className="grid lg:grid-cols-2 gap-6 mb-8">
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <h2 className="font-serif text-lg text-[#0B3A2A] mb-3">
              <BookOpen className="inline w-4 h-4 text-[#C9A961] mr-1" /> Articles par cluster
            </h2>
            {(stats?.by_cluster?.length || 0) === 0 ? (
              <p className="text-sm text-slate-500 italic">Aucun article généré pour l'instant.</p>
            ) : (
              <ul className="space-y-1.5" data-testid="by-cluster">
                {stats.by_cluster.slice(0, 10).map(c => (
                  <li key={c.cluster} className="flex items-center justify-between text-sm">
                    <span className="text-slate-700">{c.label}</span>
                    <span className="font-mono text-[#C9A961]">{c.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <h2 className="font-serif text-lg text-[#0B3A2A] mb-3">
              <KeySquare className="inline w-4 h-4 text-[#C9A961] mr-1" /> Top mots-clés ciblés
            </h2>
            {(stats?.top_keywords?.length || 0) === 0 ? (
              <p className="text-sm text-slate-500 italic">Pas encore de mots-clés.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="top-keywords">
                {stats.top_keywords.slice(0, 20).map(k => (
                  <span key={k.keyword}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-[#FAF7F1] border border-[#C9A961]/30 text-[#0B3A2A]">
                    {k.keyword} <span className="text-[#C9A961] font-mono">×{k.count}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* RECENT ARTICLES */}
        <section className="rounded-2xl bg-white border border-slate-200 p-6 mb-8">
          <h2 className="font-serif text-lg text-[#0B3A2A] mb-3">Derniers articles générés</h2>
          {(stats?.recent?.length || 0) === 0 ? (
            <p className="text-sm text-slate-500 italic">Aucun article pour l'instant.</p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="recent-articles">
              {stats.recent.map(a => (
                <li key={a.id} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#0B3A2A] truncate">{a.title}</p>
                    <p className="text-[11px] text-slate-500">
                      {a.created_at?.slice(0, 10)} · slug: {a.slug?.slice(0, 60)}
                    </p>
                  </div>
                  <StatusPill status={a.status} />
                  <Link to={`/insights/${a.slug}`} target="_blank" rel="noreferrer"
                        className="text-xs text-[#C9A961] hover:text-[#0B3A2A] shrink-0">
                    Voir <ArrowRight className="inline w-3 h-3 ml-0.5" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* QUEUE */}
        <section className="rounded-2xl bg-white border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-lg text-[#0B3A2A]">
              File d'attente ({topics.length})
            </h2>
            <Link to="/admin/contents" className="text-xs text-[#C9A961] hover:text-[#0B3A2A]">
              Voir tous les drafts <ArrowRight className="inline w-3 h-3 ml-0.5" />
            </Link>
          </div>
          {topics.length === 0 ? (
            <p className="text-sm text-slate-500 italic">File vide. Cliquez sur « Seed » pour pré-charger 107 sujets.</p>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto" data-testid="queue">
              {topics.map(t => (
                <li key={t.id} className="py-2 flex items-center gap-3 text-sm">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#0B3A2A] text-white shrink-0">
                    P{t.priority}
                  </span>
                  <span className="text-[10px] text-slate-500 shrink-0 w-40 truncate">
                    {t.cluster}
                  </span>
                  <span className="text-slate-700 flex-1 truncate">{t.title}</span>
                  {t.city && <span className="text-[10px] text-[#C9A961] shrink-0">📍 {t.city}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <p className="font-serif text-3xl text-[#0B3A2A]">{value}</p>
      <p className="text-[11px] text-slate-500 mt-1">{sub}</p>
    </div>
  );
}

function StatusPill({ status }) {
  const cfg = {
    draft: { bg: "#fef3c7", color: "#92400e", label: "Draft" },
    pending: { bg: "#dbeafe", color: "#0B3A2A", label: "À valider" },
    published: { bg: "#dcfce7", color: "#166534", label: "Publié" },
    rejected: { bg: "#fee2e2", color: "#991b1b", label: "Rejeté" },
  }[status] || { bg: "#f1f5f9", color: "#475569", label: status };
  return (
    <span className="text-[10px] px-2 py-0.5 rounded-full shrink-0"
          style={{ background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}
